import { NoStockLocationNotice } from '../../app/no-stock-location-notice.js';
import {
  commissionInputOf,
  commissionProblem,
  commissionReview,
  initialCommission,
  reconcileCommission,
  ruleOfLine,
  showsCommissionField,
  type LineCommission,
} from './sale-commission.js';
import { SaleCommissionField } from './sale-commission-field.js';
import { digitsOf, isValidCpf } from '@hue/contracts';
import {
  Button,
  Combobox,
  IconButton,
  Input,
  SegmentedControl,
  Select,
  StatusPill,
  Switch,
  Tooltip,
  formatCivilDate,
  formatCurrency,
  readsAsPlural,
  useSnackbar,
  useSnackbarSpace,
} from '@hue/design-system';
import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  LayoutGrid,
  List,
  Lock,
  Minus,
  PackageOpen,
  Plus,
  Search,
  ShoppingBag,
  Trash2,
  UserRound,
  Split,
  Layers,
} from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import {
  EmptyCard,
  ErrorCard,
  LoadingCard,
  PageSizePicker,
  Pagination,
  RequestAlert,
} from '../../app/data-view.js';
import {
  SALE_CATALOG_PAGE_SIZES,
  type SalesCatalogKind,
  type SalesView,
} from '../../app/list-views.js';
import { PageHeader } from '../../app/page-title.js';
import type { ViewSetter } from '../../app/view-state.js';
import {
  onlinePaymentsApi,
  cardsApi,
  clientsApi,
  getApiError,
  getApiErrorCode,
  commissionsApi,
  salesApi,
  schedulingApi,
  type Sale,
  type SaleDraftInput,
  type SalesCatalogPageEntry,
} from '../../lib/api.js';
import { civilDateOf, formatDateTime } from '../../lib/time-zone.js';
import {
  basisPointsOf,
  chosenLotsOf,
  entryLine,
  grossOf,
  lotPlan,
  percentOf,
  saleItemsOf,
  saleLines,
  stockNeeds,
  stockProblem,
  type AmountMode,
  type CartLine,
  type StockItem,
} from './sale-cart.js';
import { AmountField } from './amount-field.js';
import { fitToViewport } from './fit-to-viewport.js';
import { isStockRefusal } from './sale-fulfillment.js';
import { paymentTotals } from './sale-payment-totals.js';
import { SalePaymentsDialog } from './sale-payments-dialog.js';

const SEARCH_DELAY_MS = 250;
const APPOINTMENT_LINE = 'appointment-service';
const KIND_LABEL = { SERVICE: 'Serviço', PRODUCT: 'Produto', PACKAGE: 'Pacote' } as const;
type Method = 'PIX' | 'CASH' | 'CARD' | 'OTHER' | 'CREDIT' | 'ONLINE' | 'LATER';
type Person = { id: string; name: string };

export interface SaleWorkspaceCapabilities {
  canUseCredit: boolean;
  canManagePayments?: boolean;
  financeContracted?: boolean;
  /// Cartão com o Financeiro: as parcelas vêm da política da unidade.
  cardsEnabled: boolean;
  cardAllowed: boolean;
  /// A empresa contratou as comissões: cada item mostra quem recebe (ADR 0082).
  commissionsEnabled?: boolean;
}

/// A Nova venda em tela única (ADR 0059): o catálogo à esquerda e a venda, sempre à vista, à
/// direita. Aberta pelo atendimento, a venda já vem com o cliente e o serviço dele — ou, quando a
/// venda do atendimento já existe, recebe itens novos sem mexer no que ela tem.
export function SaleWorkspace({
  capabilities,
  view,
  onView,
  onBack,
  onOpenAppointment,
  initialClient,
  embedded = false,
  requiredServiceId,
  onCompleted,
}: {
  embedded?: boolean | undefined;
  initialClient?: Person | undefined;
  requiredServiceId?: string | undefined;
  onCompleted?: ((sale: Sale) => void) | undefined;
  capabilities: SaleWorkspaceCapabilities;
  view: SalesView;
  onView: ViewSetter<SalesView>;
  onBack: () => void;
  onOpenAppointment?: ((appointmentId: string) => void) | undefined;
}) {
  const queryClient = useQueryClient();
  const onlineStatus = useQuery({
    queryKey: ['company', 'online-payments', 'status'],
    queryFn: () => onlinePaymentsApi.status(),
    enabled: Boolean(capabilities.canManagePayments),
    retry: false,
  });
  const [onlineSale, setOnlineSale] = useState<Sale>();
  const snackbar = useSnackbar();
  const appointmentId = view.appointment;
  const appointment = useQuery({
    queryKey: ['company', 'scheduling', 'appointment', appointmentId],
    queryFn: () => schedulingApi.getAppointment(appointmentId!),
    enabled: Boolean(appointmentId),
    retry: false,
  });
  const appointmentSale = useQuery({
    queryKey: ['company', 'sales', 'appointment', appointmentId],
    queryFn: () => salesApi.appointmentSale(appointmentId!),
    enabled: Boolean(appointmentId),
    retry: false,
  });
  const context =
    appointmentId && appointment.data && appointmentSale.isSuccess
      ? {
          appointment: appointment.data,
          sale: appointmentSale.data,
          prepaid: Boolean(appointment.data.prepaidBalanceId),
        }
      : null;

  const [lines, setLines] = useState<CartLine[]>([]);
  const commissionOptions = useQuery({
    queryKey: [
      'company',
      'sales',
      'commission-options',
      appointment.data?.assistantId ?? '',
      appointment.data?.professionalId ?? null,
    ],
    queryFn: () =>
      commissionsApi.saleOptions(
        appointment.data
          ? {
              fromAppointment: true,
              appointmentProfessionalId: appointment.data.professionalId,
              ...(appointment.data.assistantId
                ? { appointmentAssistantId: appointment.data.assistantId }
                : {}),
            }
          : {},
      ),
    enabled:
      Boolean(capabilities.commissionsEnabled) && (!appointmentId || Boolean(appointment.data)),
    retry: false,
  });
  /// O que a pessoa mudou em cada linha; a linha sem escolha segue o padrão (ADR 0082, decisão 4).
  const [commissions, setCommissions] = useState<Record<string, LineCommission>>({});
  const commissionOf = (key: string, quantity: number) =>
    reconcileCommission(
      commissions[key] ?? {
        ...initialCommission(commissionOptions.data?.defaultResourceId ?? null, quantity),
        assistantResourceId:
          key === APPOINTMENT_LINE
            ? (commissionOptions.data?.defaultAssistantResourceId ?? null)
            : null,
      },
      quantity,
    );
  const nameOfReceiver = (resourceId: string) =>
    commissionOptions.data?.receivers.find((one) => one.id === resourceId)?.displayName ??
    'Profissional';
  const [client, setClient] = useState<Person | null>(initialClient ?? null);
  const [chosenLocation, setLocationId] = useState('');
  const [manual, setManual] = useState<Record<string, Record<string, number>>>({});
  const [discountMode, setDiscountMode] = useState<AmountMode>('BRL');
  const [discountCents, setDiscountCents] = useState(0);
  const [discountBp, setDiscountBp] = useState(0);
  const [method, setMethod] = useState<Method>('PIX');
  const [receivedMode, setReceivedMode] = useState<AmountMode>('BRL');
  const [receivedCents, setReceivedCents] = useState<number | null>(null);
  const [receivedBp, setReceivedBp] = useState(10_000);
  const [installmentCount, setInstallmentCount] = useState(1);
  const [cardValid, setCardValid] = useState(false);
  const [useSession, setUseSession] = useState<string | null>(null);
  const [error, setError] = useState<string>();
  const [stockError, setStockError] = useState<string>();
  const [saving, setSaving] = useState(false);
  const orderRef = useRef<HTMLElement>(null);
  /// A barra do celular sai da frente do aviso, como o botão de voltar ao topo.
  const snackbarSpace = useSnackbarSpace();
  /// No celular, a barra some enquanto a própria venda está na tela: ali ela só cobriria o campo em
  /// uso, como a lista de clientes.
  const [orderInView, setOrderInView] = useState(false);
  const orderWatch = useRef<IntersectionObserver | null>(null);
  const orderFit = useRef<(() => void) | null>(null);
  const attachOrder = useCallback((node: HTMLElement | null) => {
    orderRef.current = node;
    orderFit.current?.();
    orderFit.current = node ? fitToViewport(node) : null;
    orderWatch.current?.disconnect();
    orderWatch.current = null;
    if (!node || typeof IntersectionObserver === 'undefined') return;
    orderWatch.current = new IntersectionObserver(([entry]) =>
      setOrderInView(Boolean(entry?.isIntersecting)),
    );
    orderWatch.current.observe(node);
  }, []);

  /// O cliente da venda do atendimento é o do agendamento, e não se troca.
  const buyer: Person | null = context
    ? { id: context.appointment.clientId, name: context.appointment.clientDisplayName }
    : client;

  const needs = useMemo(() => stockNeeds(lines), [lines]);
  const [knownLocations, setKnownLocations] = useState<Array<{ id: string; displayName: string }>>(
    [],
  );
  /// Começa pelo primeiro local ativo da unidade; a pessoa troca no bloco da saída.
  const locationId = chosenLocation || knownLocations[0]?.id || '';
  const stock = useQuery({
    queryKey: [
      'company',
      'sales',
      'stock-options',
      locationId,
      needs.map((need) => `${need.referenceId}:${String(need.quantity)}`).join(','),
    ],
    queryFn: () =>
      salesApi.stockOptions({
        locationId: locationId || undefined,
        items: needs.map((need) => ({ referenceId: need.referenceId, quantity: need.quantity })),
      }),
    placeholderData: keepPreviousData,
    retry: false,
  });
  const locations = stock.data?.locations ?? knownLocations;
  if (stock.data && stock.data.locations.length !== knownLocations.length)
    setKnownLocations(stock.data.locations);
  const locationName = locations.find((location) => location.id === locationId)?.displayName ?? '';
  const stockOf = (referenceId: string): StockItem | undefined =>
    stock.data?.items.find((item) => item.referenceId === referenceId);

  /// A venda do atendimento: o serviço dele, quando ainda não há venda e a sessão não é de pacote.
  const serviceInSale = context && !context.sale && !context.prepaid && !useSession;
  const existing = context?.sale ?? null;
  const serviceItem = existing?.items.find((item) => item.appointmentService && !item.cancellation);
  const cartGross = grossOf(lines);
  const discountBase = cartGross + (serviceInSale ? context.appointment.priceCents : 0);
  const discount =
    discountMode === 'PCT'
      ? percentOf(discountBase, discountBp)
      : Math.min(discountCents, discountBase);
  /// Em reais, o desconto fica como foi digitado: não cresce com os itens nem é cortado sozinho.
  const discountOver = discountMode === 'BRL' && discountCents > discountBase;
  const lockedNet = existing
    ? existing.netTotalCents - (useSession && serviceItem ? serviceItem.allocatedTotalCents : 0)
    : 0;
  const total = lockedNet + discountBase - discount;
  const committed = existing
    ? paymentTotals(existing).confirmedCents + paymentTotals(existing).awaitingCents
    : 0;
  const due = Math.max(total - committed, 0);
  const received =
    method === 'ONLINE' || method === 'LATER'
      ? 0
      : receivedMode === 'PCT'
        ? percentOf(due, receivedBp)
        : (receivedCents ?? due);
  const problems = needs.flatMap((need) => {
    const problem = stockProblem(
      need,
      stockOf(need.referenceId),
      manual[need.referenceId],
      locationName,
    );
    return problem ? [problem] : [];
  });
  const closed =
    existing?.status === 'CANCELED' || (!existing && context?.appointment.status === 'CANCELLED');

  function room(referenceId: string, extra: number, known?: number): boolean {
    const available = stockOf(referenceId)?.available ?? known;
    if (available === undefined) return true;
    const inSale = needs.find((need) => need.referenceId === referenceId)?.quantity ?? 0;
    return inSale + extra <= available;
  }

  function add(entry: Omit<CartLine, 'quantity'>, known?: number, undo = true) {
    if (requiredServiceId && !entry.balanceServiceIds.includes(requiredServiceId)) {
      setError('Escolha um pacote com sessões por saldo do serviço deste agendamento.');
      return;
    }
    for (const product of entry.products)
      if (!room(product.referenceId, product.quantity, known)) {
        snackbar.failure(`Não há mais ${product.label} em ${locationName || 'estoque'}.`);
        return;
      }
    setLines((current) =>
      current.some((line) => line.key === entry.key)
        ? current.map((line) =>
            line.key === entry.key ? { ...line, quantity: line.quantity + 1 } : line,
          )
        : [
            ...current,
            {
              ...entry,
              quantity: 1,
              ...(entry.requiresBeneficiary && buyer ? { beneficiary: buyer } : {}),
            },
          ],
    );
    setError(undefined);
    if (undo)
      snackbar.success(`${entry.label} adicionado à venda.`, {
        label: 'Desfazer',
        onAction: () => changeQuantity(entry.key, -1),
      });
  }

  function changeQuantity(key: string, delta: number) {
    setLines((current) =>
      current.flatMap((line) => {
        if (line.key !== key) return [line];
        const quantity = line.quantity + delta;
        return quantity > 0 ? [{ ...line, quantity }] : [];
      }),
    );
  }

  function setQuantity(key: string, quantity: number) {
    setLines((current) => current.map((line) => (line.key === key ? { ...line, quantity } : line)));
  }

  function remove(key: string) {
    const removed = lines.find((line) => line.key === key);
    setLines((current) => current.filter((line) => line.key !== key));
    if (removed?.kind === 'PACKAGE' && useSession === removed.id) setUseSession(null);
    if (removed)
      snackbar.success(`${removed.label} removido.`, {
        label: 'Desfazer',
        onAction: () => setLines((current) => [...current, removed]),
      });
  }

  /// O atalho "Vender" de Serviços e de Produtos chega pela URL: o item entra uma vez na venda e
  /// os dois parâmetros saem da URL.
  const handledSell = useRef<string | undefined>(undefined);
  const pendingSell = view.sell;
  useEffect(() => {
    if (!pendingSell) {
      handledSell.current = undefined;
      return;
    }
    if (handledSell.current === pendingSell) return;
    handledSell.current = pendingSell;
    void salesApi
      .catalogPage({ kind: 'ALL', search: view.search.trim(), limit: 50, offset: 0 })
      .then((page) => page.items)
      .catch(() => [])
      .then((items) => {
        const found = items.find((item) => `${item.kind}:${item.id}` === pendingSell);
        if (found) add(entryLine(found), undefined, false);
        else snackbar.failure('Esse item não está disponível para venda.');
        onView({ sell: undefined, search: '' }, { replace: true });
      });
    // A busca pelo nome só vale junto do atalho que a trouxe.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingSell]);

  /// O serviço do agendamento entra sozinho, mas a comissão dele também tem campo (decisão 4).
  const appointmentCommissionRule =
    context && !existing && !context.prepaid && !useSession
      ? commissionOptions.data?.rules.find(
          (rule) =>
            rule.itemKind === 'SERVICE' && rule.referenceId === context.appointment.serviceId,
        )
      : undefined;
  /// O resumo antes de confirmar: quem recebe, sem valores (decisão 7).
  const commissionReviewLines = [
    ...(appointmentCommissionRule && context
      ? [
          {
            key: APPOINTMENT_LINE,
            label: context.appointment.serviceDisplayName,
            commission: commissionOf(APPOINTMENT_LINE, 1),
          },
        ]
      : []),
    ...lines
      .filter((line) => showsCommissionField(commissionOptions.data, line))
      .map((line) => ({
        key: line.key,
        label: line.label,
        commission: commissionOf(line.key, line.quantity),
      })),
  ].map((one) => ({ ...one, problem: commissionProblem(one.commission) }));

  function reset() {
    setLines([]);
    setCommissions({});
    setClient(null);
    setManual({});
    setDiscountCents(0);
    setDiscountBp(0);
    setReceivedCents(null);
    setReceivedBp(10_000);
    setReceivedMode('BRL');
    setUseSession(null);
    setMethod('PIX');
  }

  async function confirm() {
    setError(undefined);
    setStockError(undefined);
    if (lines.some((line) => !Number.isSafeInteger(line.quantity) || line.quantity < 1)) {
      setError('Informe uma quantidade inteira maior que zero para cada item.');
      return;
    }
    if (lines.some((line) => line.requiresBeneficiary && !line.beneficiary)) {
      setError('Escolha quem recebe o saldo de cada pacote.');
      return;
    }
    if (needs.length && !locationId) {
      setStockError('Escolha o local de estoque de onde os produtos saem.');
      return;
    }
    if (problems.length) {
      setStockError(problems.join(' '));
      return;
    }
    if (method === 'CREDIT' && !buyer) {
      setError('Identifique o cliente para usar o crédito dele.');
      return;
    }
    if (method === 'CARD' && capabilities.cardsEnabled && received > 0 && !cardValid) {
      setError('Confira as parcelas do cartão antes de confirmar.');
      return;
    }
    if (received > due) {
      setError(
        `O valor recebido passa do que falta receber (${formatCurrency(due, 'BRL')}). Excedente vira crédito pelos Pagamentos da venda.`,
      );
      return;
    }
    if (
      method === 'ONLINE' &&
      (!buyer || !capabilities.canManagePayments || !onlineStatus.data?.available)
    ) {
      setError(
        'Para gerar cobrança online, identifique o cliente e conecte a cobrança online da unidade.',
      );
      return;
    }
    const reviewed = commissionReviewLines.find((one) => one.problem);
    if (reviewed?.problem) {
      setError(`${reviewed.label}: ${reviewed.problem}`);
      return;
    }
    const draft: SaleDraftInput = {
      discountCents: discountMode === 'BRL' ? discount : 0,
      ...(discountMode === 'PCT' && discountBp > 0
        ? { discountPercentBasisPoints: discountBp }
        : {}),
      ...(useSession ? { useTodaySessionOfPackageId: useSession } : {}),
      soldAt: new Date().toISOString(),
      items: saleItemsOf(lines, (line) =>
        showsCommissionField(commissionOptions.data, line)
          ? commissionInputOf(commissionOf(line.key, line.quantity))
          : undefined,
      ),
      payments:
        received > 0 && method !== 'ONLINE' && method !== 'LATER'
          ? [
              {
                method,
                status: 'CONFIRMED',
                amountCents: received,
                ...(method === 'CARD' && capabilities.cardsEnabled
                  ? { card: { installmentCount } }
                  : {}),
              },
            ]
          : [],
      ...(needs.length ? { stock: { locationId, lots: chosenLotsOf(needs, manual) } } : {}),
    };
    setSaving(true);
    try {
      const sale = existing
        ? await salesApi.addSaleItems(existing.id, draft)
        : await salesApi.createSale({
            ...draft,
            ...(context
              ? {
                  appointmentId: context.appointment.id,
                  // Sem escolha à mão, a API usa o padrão e o campo acompanha o atendimento (decisão 4).
                  ...(appointmentCommissionRule && commissions[APPOINTMENT_LINE]
                    ? {
                        appointmentCommission: commissionInputOf(commissionOf(APPOINTMENT_LINE, 1)),
                      }
                    : {}),
                }
              : { clientId: client?.id ?? null }),
          });
      await queryClient.invalidateQueries({ queryKey: ['company', 'sales'] });
      await queryClient.invalidateQueries({ queryKey: ['company', 'scheduling'] });
      if (method === 'ONLINE') {
        setOnlineSale(sale);
        return;
      }
      if (context) {
        snackbar.success(
          existing
            ? `Itens adicionados à venda ${String(sale.number)}.`
            : `Venda ${String(sale.number)} do atendimento registrada.`,
        );
        reset();
        onBack();
        return;
      }
      snackbar.success(
        lines.some((line) => line.kind === 'SERVICE')
          ? `Venda ${String(sale.number)} registrada. Agende os serviços pelo histórico.`
          : `Venda ${String(sale.number)} registrada.`,
      );
      reset();
      onCompleted?.(sale);
    } catch (reason) {
      // Recusa do Estoque (saldo, lote, local) se corrige no bloco da saída, junto do local.
      if (isStockRefusal(getApiErrorCode(reason))) setStockError(getApiError(reason));
      else setError(getApiError(reason));
    } finally {
      setSaving(false);
    }
  }

  const units = lines.reduce((sum, line) => sum + line.quantity, 0);
  const loadingContext =
    Boolean(appointmentId) && !context && !appointment.error && !appointmentSale.error;
  if (onlineSale)
    return (
      <SalePaymentsDialog
        sale={onlineSale}
        initialCharge
        canManage={Boolean(capabilities.canManagePayments)}
        financeContracted={Boolean(capabilities.financeContracted)}
        today={civilDateOf(new Date(), onlineSale.timeZone ?? 'UTC')}
        onChanged={() => queryClient.invalidateQueries({ queryKey: ['company', 'sales'] })}
        onClose={() => {
          const completed = onlineSale;
          setOnlineSale(undefined);
          reset();
          if (context) onBack();
          else onCompleted?.(completed);
        }}
      />
    );

  const header = !embedded ? (
    <PageHeader
      onBack={onBack}
      trail={appointmentId ? 'Operação / Agenda / Atendimento' : 'Operação / Vendas'}
      id="sales-title"
      title={existing ? `Adicionar à venda ${String(existing.number)}` : 'Nova venda'}
      description="Escolha no catálogo; a venda fica sempre à vista, com quantidades, saída do estoque e pagamento."
    />
  ) : null;
  return (
    <section
      {...(embedded ? { 'aria-label': 'Nova venda' } : { 'aria-labelledby': 'sales-title' })}
    >
      {appointment.error || appointmentSale.error ? (
        <>
          {header}
          <ErrorCard message={getApiError(appointment.error ?? appointmentSale.error)} />
        </>
      ) : loadingContext ? (
        <>
          {header}
          <LoadingCard label="Carregando o atendimento" />
        </>
      ) : (
        // No computador o cabeçalho fica só na coluna do catálogo, e o card da venda começa na
        // altura do título, aproveitando a coluna direita inteira.
        <>
          <div className="grid items-start gap-5 pb-24 lg:grid-cols-[minmax(0,1fr)_minmax(22rem,26rem)] lg:pb-0">
            <div className="min-w-0">
              {header}
              {context ? (
                <div className="mb-4 flex flex-wrap items-center justify-between gap-3 rounded-xl bg-brand-500/10 px-4 py-3 text-sm">
                  <span>
                    {existing ? `Adicionando à venda ${String(existing.number)}, do ` : 'Venda do '}
                    atendimento de <strong>{context.appointment.clientDisplayName}</strong> em{' '}
                    {formatDateTime(
                      context.appointment.startsAt,
                      context.appointment.timeZone ?? 'UTC',
                    )}
                    .{' '}
                    {existing
                      ? 'O que já está na venda não muda.'
                      : context.prepaid
                        ? 'A sessão é do pacote e já está paga; a venda leva só o que for adicionado.'
                        : 'O serviço já está na venda; adicione o que mais o cliente levar.'}
                  </span>
                  {onOpenAppointment ? (
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => onOpenAppointment(context.appointment.id)}
                    >
                      Voltar ao atendimento
                    </Button>
                  ) : null}
                </div>
              ) : null}
              <SaleCatalog
                view={view}
                requiredServiceId={requiredServiceId}
                onView={onView}
                locationId={locationId}
                inSale={(key) => lines.find((line) => line.key === key)?.quantity ?? 0}
                onAdd={(entry, known) => add(entry, known)}
                onChange={changeQuantity}
              />
            </div>
            <aside
              ref={attachOrder}
              aria-label="Venda"
              className="flex flex-col rounded-2xl border border-surface-border bg-surface-card shadow-soft lg:sticky lg:top-4 lg:max-h-[calc(100dvh-2rem)] lg:overflow-hidden"
            >
              <div className="flex items-center justify-between gap-2 border-b border-surface-border px-4 py-3">
                <h2 className="text-lg font-semibold">
                  Venda{' '}
                  {units ? (
                    <span className="text-sm font-normal text-content-muted">
                      · {units}{' '}
                      {units === 1
                        ? context
                          ? 'item novo'
                          : 'item'
                        : context
                          ? 'itens novos'
                          : 'itens'}
                    </span>
                  ) : null}
                </h2>
                {lines.length ? (
                  <Button size="sm" variant="ghost" onClick={() => setLines([])}>
                    Limpar
                  </Button>
                ) : null}
              </div>
              <div className="grid gap-4 p-4 lg:min-h-0 lg:flex-1 lg:overflow-y-auto lg:overscroll-contain">
                <Block title="Cliente">
                  {context || initialClient ? (
                    <PersonRow
                      name={context?.appointment.clientDisplayName ?? initialClient!.name}
                      note={context ? 'Cliente do atendimento' : 'Cliente do agendamento'}
                    />
                  ) : (
                    <ClientPicker
                      label="Cliente"
                      hideLabel
                      selected={client}
                      onSelect={(person) => {
                        setClient(person);
                        if (person) {
                          setLines((current) =>
                            current.map((line) =>
                              line.requiresBeneficiary && !line.beneficiary
                                ? { ...line, beneficiary: person }
                                : line,
                            ),
                          );
                        }
                        if (!person && method === 'CREDIT') setMethod('PIX');
                      }}
                      hint="Sem cliente, é venda de balcão."
                    />
                  )}
                </Block>
                {context ? (
                  <Block title="Do atendimento">
                    {context.prepaid ? (
                      <LockedRow
                        label={context.appointment.serviceDisplayName}
                        note="Sessão do pacote · já paga"
                        value="Não entra na venda"
                      />
                    ) : null}
                    {!existing && !context.prepaid ? (
                      <LockedRow
                        label={context.appointment.serviceDisplayName}
                        note={
                          useSession
                            ? 'Vira uma sessão do pacote escolhido'
                            : `Serviço do atendimento de ${formatDateTime(context.appointment.startsAt, context.appointment.timeZone ?? 'UTC')}`
                        }
                        value={
                          useSession
                            ? 'Sai da venda'
                            : formatCurrency(context.appointment.priceCents, 'BRL')
                        }
                        out={Boolean(useSession)}
                      />
                    ) : null}
                    {appointmentCommissionRule ? (
                      <SaleCommissionField
                        label={context.appointment.serviceDisplayName}
                        quantity={1}
                        commission={commissionOf(APPOINTMENT_LINE, 1)}
                        receivers={commissionOptions.data?.receivers ?? []}
                        assistant={appointmentCommissionRule.assistant}
                        onChange={(next) =>
                          setCommissions((current) => ({ ...current, [APPOINTMENT_LINE]: next }))
                        }
                      />
                    ) : null}
                    {existing
                      ? saleLines(existing).map((line) => (
                          <LockedRow
                            key={line.key}
                            label={line.label}
                            note={
                              useSession && line.appointmentService
                                ? 'Vira uma sessão do pacote escolhido'
                                : `Já na venda ${String(existing.number)} · preço mantido`
                            }
                            value={
                              useSession && line.appointmentService
                                ? 'Sai da venda'
                                : formatCurrency(line.cents, existing.currency)
                            }
                            out={Boolean(useSession && line.appointmentService)}
                          />
                        ))
                      : null}
                    {closed ? (
                      <RequestAlert
                        message={
                          existing
                            ? `A venda ${String(existing.number)} foi cancelada e não recebe itens.`
                            : 'Atendimento cancelado não gera venda.'
                        }
                      />
                    ) : null}
                  </Block>
                ) : null}
                <Block title={context ? 'Itens novos' : 'Itens'}>
                  {lines.length === 0 ? (
                    <div className="grid justify-items-center gap-1 py-4 text-center text-sm text-content-muted">
                      <ShoppingBag className="size-6" />
                      <strong className="text-content">
                        {context ? 'Nada novo ainda' : 'Nenhum item ainda'}
                      </strong>
                      <span>
                        {context
                          ? 'O cliente vai levar mais alguma coisa? Adicione pelo catálogo.'
                          : 'Adicione serviços, produtos ou pacotes pelo catálogo.'}
                      </span>
                    </div>
                  ) : (
                    <ul className="grid gap-3">
                      {lines.map((line) => (
                        <CartRow
                          key={line.key}
                          line={line}
                          problem={line.products
                            .map((product) =>
                              problems.find((problem) => problem.startsWith(`${product.label}:`)),
                            )
                            .find(Boolean)}
                          canIncrease={line.products.every((product) =>
                            room(product.referenceId, product.quantity),
                          )}
                          commission={
                            showsCommissionField(commissionOptions.data, line) ? (
                              <SaleCommissionField
                                label={line.label}
                                quantity={line.quantity}
                                commission={commissionOf(line.key, line.quantity)}
                                receivers={commissionOptions.data?.receivers ?? []}
                                assistant={Boolean(
                                  ruleOfLine(commissionOptions.data, line)?.assistant,
                                )}
                                onChange={(next) =>
                                  setCommissions((current) => ({ ...current, [line.key]: next }))
                                }
                              />
                            ) : ruleOfLine(commissionOptions.data, line)?.packageMode ===
                              'PER_SESSION' ? (
                              <p className="text-xs text-content-muted">
                                Comissão por sessão realizada: recebe quem atender.
                              </p>
                            ) : null
                          }
                          onChange={(delta) => changeQuantity(line.key, delta)}
                          onQuantity={(quantity) => setQuantity(line.key, quantity)}
                          onRemove={() => remove(line.key)}
                          onBeneficiary={(person) =>
                            setLines((current) =>
                              current.map((entry) =>
                                entry.key === line.key
                                  ? { ...entry, beneficiary: person ?? undefined }
                                  : entry,
                              ),
                            )
                          }
                          onSplit={() =>
                            setLines((current) => [
                              ...current.map((entry) =>
                                entry.key === line.key
                                  ? { ...entry, quantity: entry.quantity - 1 }
                                  : entry,
                              ),
                              { ...line, key: `${line.key}:${crypto.randomUUID()}`, quantity: 1 },
                            ])
                          }
                          session={
                            context &&
                            !context.prepaid &&
                            (!existing || serviceItem) &&
                            line.kind === 'PACKAGE' &&
                            line.balanceServiceIds.includes(context.appointment.serviceId) &&
                            line.beneficiary?.id === context.appointment.clientId &&
                            (!useSession || useSession === line.id)
                              ? {
                                  on: useSession === line.id,
                                  serviceName: context.appointment.serviceDisplayName,
                                  onToggle: (on) => setUseSession(on ? line.id : null),
                                }
                              : undefined
                          }
                        />
                      ))}
                    </ul>
                  )}
                </Block>
                {needs.length ? (
                  <StockBlock
                    needs={needs}
                    locations={locations}
                    locationId={locationId}
                    onLocation={(next) => {
                      setLocationId(next);
                      setManual({});
                      setStockError(undefined);
                    }}
                    stockOf={stockOf}
                    problemOf={(need) =>
                      stockProblem(
                        need,
                        stockOf(need.referenceId),
                        manual[need.referenceId],
                        locationName,
                      )
                    }
                    manual={manual}
                    onManual={setManual}
                    error={stockError}
                    loading={stock.isLoading}
                    failure={stock.error ? getApiError(stock.error) : undefined}
                  />
                ) : null}
                <Block title="Pagamento">
                  {existing ? (
                    <p className="text-xs tabular-nums text-content-muted">
                      Já recebido nesta venda:{' '}
                      <strong>
                        {formatCurrency(paymentTotals(existing).confirmedCents, existing.currency)}
                      </strong>
                      {paymentTotals(existing).awaitingCents > 0
                        ? ` · aguardando ${formatCurrency(paymentTotals(existing).awaitingCents, existing.currency)}`
                        : ''}{' '}
                      · {readsAsPlural(due) ? 'faltam' : 'falta'}{' '}
                      {formatCurrency(due, existing.currency)}
                    </p>
                  ) : null}
                  <span aria-hidden className="text-xs font-semibold text-content/70">
                    Como receber
                  </span>
                  <SegmentedControl
                    label="Como receber"
                    value={method === 'ONLINE' ? 'ONLINE' : method === 'LATER' ? 'LATER' : 'MANUAL'}
                    onChange={(next) => setMethod(next === 'MANUAL' ? 'PIX' : next)}
                    options={[
                      {
                        value: 'MANUAL' as const,
                        label: 'Recebido',
                        accessibleLabel: 'Registrar pagamento recebido',
                      },
                      ...(capabilities.canManagePayments && onlineStatus.data?.available
                        ? [
                            {
                              value: 'ONLINE' as const,
                              // Sem isso o navegador quebra no hífen: "On-" em cima, "line" embaixo.
                              label: (
                                <>
                                  Cobrança <span className="whitespace-nowrap">On-line</span>
                                </>
                              ),
                              accessibleLabel: 'Gerar Cobrança On-line',
                            },
                          ]
                        : []),
                      {
                        value: 'LATER' as const,
                        label: 'Depois',
                        accessibleLabel: 'Receber depois',
                      },
                    ]}
                  />
                  {method !== 'ONLINE' && method !== 'LATER' ? (
                    <>
                      <p className="text-xs text-content-muted">
                        Registre somente o que já foi recebido e conferido no banco ou na
                        maquininha. O sistema não consulta esses recebimentos automaticamente. Pix
                        pela maquininha também é Pix.
                      </p>
                      <span aria-hidden className="text-xs font-semibold text-content/70">
                        Meio de pagamento
                      </span>
                      <SegmentedControl
                        label="Meio de pagamento"
                        value={method}
                        onChange={(next) => {
                          setMethod(next);
                          if (next === 'CREDIT') {
                            setReceivedMode('BRL');
                            setReceivedCents(null);
                          }
                        }}
                        options={[
                          { value: 'PIX' as const, label: 'PIX' },
                          { value: 'CASH' as const, label: 'Dinheiro' },
                          ...(capabilities.cardAllowed
                            ? [{ value: 'CARD' as const, label: 'Cartão' }]
                            : []),
                          { value: 'OTHER' as const, label: 'Outro' },

                          ...(capabilities.canUseCredit && buyer
                            ? [{ value: 'CREDIT' as const, label: 'Crédito' }]
                            : []),
                        ]}
                      />
                    </>
                  ) : null}
                  {method === 'CARD' && capabilities.cardsEnabled ? (
                    <CardPaymentFields
                      installmentCount={installmentCount}
                      onCount={setInstallmentCount}
                      onValid={setCardValid}
                    />
                  ) : null}
                  {method === 'ONLINE' ? (
                    <p className="text-xs text-content-muted">
                      A venda será registrada sem recebimento. Em seguida, confira o valor e o
                      vencimento para gerar o Pix e o link aqui mesmo. Se a cobrança falhar, tente
                      novamente sem repetir a venda.
                    </p>
                  ) : method === 'LATER' ? (
                    <p className="text-xs text-content-muted">
                      A venda será confirmada com o total em aberto, sem pagamento e sem cobrança
                      automática. Não é rascunho: produtos saem do estoque e pacotes seguem suas
                      regras de liberação.
                    </p>
                  ) : (
                    <div className="mt-1 grid gap-2 border-t border-dashed border-surface-border pt-3">
                      <AmountField
                        label={method === 'CREDIT' ? 'Valor do crédito' : 'Valor recebido agora'}
                        mode={receivedMode}
                        onMode={(mode) => {
                          if (mode === 'PCT') setReceivedBp(basisPointsOf(received, due));
                          else setReceivedCents(received);
                          setReceivedMode(mode);
                        }}
                        cents={received}
                        onCents={setReceivedCents}
                        basisPoints={receivedBp}
                        onBasisPoints={setReceivedBp}
                        baseCents={due}
                        percentAllowed={method !== 'CREDIT'}
                      />
                      {method !== 'CREDIT' ? (
                        <div className="flex flex-wrap gap-2">
                          <Button
                            size="sm"
                            variant={
                              receivedMode === 'PCT' && receivedBp === 10_000
                                ? 'primary'
                                : 'outline'
                            }
                            onClick={() => {
                              setReceivedMode('PCT');
                              setReceivedBp(10_000);
                            }}
                          >
                            {existing ? 'Restante' : 'Total'}
                          </Button>
                        </div>
                      ) : null}
                      <p className="text-xs tabular-nums text-content-muted">
                        {received < due
                          ? `Fica em aberto na venda: ${formatCurrency(due - received, 'BRL')}`
                          : 'Pode ser só uma parte; o restante fica em aberto na venda.'}
                        {received === 0
                          ? ' Nenhum pagamento será registrado. Depois de confirmar, abra Vendas e os Pagamentos da venda para registrar o recebimento ou gerar uma cobrança online.'
                          : ''}
                      </p>
                    </div>
                  )}
                </Block>
              </div>
              <div className="grid gap-3 rounded-b-2xl border-t border-brand-500/30 bg-brand-500/10 p-4">
                <div className="grid gap-2 text-sm">
                  <Row
                    label={existing ? 'Itens novos' : 'Subtotal'}
                    value={formatCurrency(discountBase, 'BRL')}
                  />
                  <AmountField
                    label={existing ? 'Desconto nos itens novos' : 'Desconto'}
                    mode={discountMode}
                    onMode={(mode) => {
                      if (mode === 'PCT') setDiscountBp(basisPointsOf(discount, discountBase));
                      else setDiscountCents(discount);
                      setDiscountMode(mode);
                    }}
                    cents={discountMode === 'PCT' ? discount : discountCents}
                    onCents={(cents) => setDiscountCents(cents ?? 0)}
                    error={
                      discountOver
                        ? `Passa ${existing ? 'dos itens novos' : 'do subtotal'} (${formatCurrency(discountBase, 'BRL')}).`
                        : undefined
                    }
                    basisPoints={discountBp}
                    onBasisPoints={setDiscountBp}
                    baseCents={discountBase}
                    percentAllowed
                    inline
                    visibleLabel={
                      existing ? (
                        <>
                          Desconto <strong>nos itens novos</strong>
                        </>
                      ) : undefined
                    }
                  />
                  {existing ? (
                    <Row
                      label={`Venda ${String(existing.number)} até aqui`}
                      value={formatCurrency(lockedNet, existing.currency)}
                    />
                  ) : null}
                  <div className="flex items-center justify-between text-lg font-bold">
                    <span>Total</span>
                    <span className="tabular-nums">{formatCurrency(total, 'BRL')}</span>
                  </div>
                </div>
                {commissionReviewLines.length ? (
                  <div className="grid gap-1 text-xs">
                    <p className="font-semibold">Quem recebe a comissão</p>
                    <ul className="grid gap-0.5 text-content-muted">
                      {commissionReviewLines.map((one) => (
                        <li key={one.key}>
                          {one.label}: {commissionReview(one.commission, nameOfReceiver)}
                        </li>
                      ))}
                    </ul>
                  </div>
                ) : null}
                {error ? <RequestAlert message={error} /> : null}
                <Button
                  loading={saving}
                  disabled={closed || discountOver || (lines.length === 0 && !serviceInSale)}
                  onClick={() => void confirm()}
                >
                  {method === 'ONLINE'
                    ? 'Confirmar venda e gerar cobrança'
                    : existing
                      ? `Adicionar à venda ${String(existing.number)}`
                      : 'Confirmar venda'}{' '}
                  · {formatCurrency(due, 'BRL')}
                </Button>
                {problems.length ? (
                  <p className="text-center text-xs text-danger">
                    {problems.length === 1
                      ? '1 item precisa'
                      : `${String(problems.length)} itens precisam`}{' '}
                    de ajuste antes de confirmar.
                  </p>
                ) : null}
              </div>
            </aside>
          </div>
          <div
            style={{ bottom: snackbarSpace }}
            className={`fixed inset-x-0 bottom-0 z-30 ${orderInView ? 'hidden' : 'flex'} items-center justify-between gap-3 border-t border-surface-border bg-surface-card px-5 py-3 shadow-lg lg:hidden`}
          >
            <div>
              <strong>
                {units
                  ? `${String(units)} ${units === 1 ? 'item' : 'itens'} na venda`
                  : 'Venda vazia'}
              </strong>
              <p className="text-xs tabular-nums text-content-muted">
                Total {formatCurrency(total, 'BRL')}
              </p>
            </div>
            <Button onClick={() => orderRef.current?.scrollIntoView?.({ block: 'start' })}>
              Ver venda
            </Button>
          </div>
        </>
      )}
    </section>
  );
}

function Block({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="grid gap-2 border-t border-surface-border pt-3 first:border-t-0 first:pt-0">
      <h3 className="text-xs font-semibold uppercase tracking-wide text-content-faint">{title}</h3>
      {children}
    </div>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between gap-2">
      <span className="text-content">{label}</span>
      <span className="font-medium tabular-nums">{value}</span>
    </div>
  );
}

function PersonRow({ name, note }: { name: string; note: string }) {
  return (
    <div className="flex items-center gap-3">
      <span className="grid size-9 place-items-center rounded-full bg-brand-500/15 text-brand-600">
        <UserRound className="size-4" />
      </span>
      <div className="min-w-0">
        <p className="truncate font-semibold">{name}</p>
        <p className="text-xs text-content-muted">{note}</p>
      </div>
    </div>
  );
}

/// O que veio travado do atendimento: o serviço dele, a sessão do pacote ou o que a venda já tinha.
function LockedRow({
  label,
  note,
  value,
  out = false,
}: {
  label: string;
  note: string;
  value: string;
  out?: boolean;
}) {
  return (
    <div className="flex items-center justify-between gap-3 rounded-xl border border-dashed border-surface-border bg-content/5 px-3 py-2">
      <div className="min-w-0">
        <p className={`text-sm font-semibold ${out ? 'text-content-muted line-through' : ''}`}>
          {label}
        </p>
        <p className="text-xs text-content-muted">{note}</p>
      </div>
      <span className="inline-flex shrink-0 items-center gap-1 text-xs tabular-nums text-content-muted">
        <Lock className="size-3.5" aria-hidden="true" />
        {value}
      </span>
    </div>
  );
}

function CartRow({
  line,
  problem,
  commission,
  canIncrease,
  onChange,
  onQuantity,
  onRemove,
  onBeneficiary,
  onSplit,
  session,
}: {
  line: CartLine;
  commission?: ReactNode;
  problem: string | undefined;
  canIncrease: boolean;
  onChange: (delta: number) => void;
  onQuantity: (quantity: number) => void;
  onRemove: () => void;
  onBeneficiary: (person: Person | null) => void;
  onSplit: () => void;
  session?: { on: boolean; serviceName: string; onToggle: (on: boolean) => void } | undefined;
}) {
  return (
    <li
      className={`grid gap-2 rounded-xl border p-3 ${problem ? 'border-danger/60' : 'border-surface-border'}`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="font-semibold">{line.label}</p>
          <p className="text-xs text-content-muted">
            {KIND_LABEL[line.kind]} · {formatCurrency(line.priceCents, 'BRL')} cada
          </p>
          {line.summary ? <p className="text-xs text-content-muted">{line.summary}</p> : null}
        </div>
        <IconButton
          label={`Remover ${line.label}`}
          tone="danger"
          icon={<Trash2 className="size-4" />}
          onClick={onRemove}
        />
      </div>
      {line.requiresBeneficiary ? (
        <ClientPicker
          label={`Quem recebe o saldo de ${line.label}`}
          selected={line.beneficiary ?? null}
          onSelect={onBeneficiary}
          hint="Pode ser outra pessoa que não o comprador."
        />
      ) : null}
      {line.requiresBeneficiary && line.quantity > 1 ? (
        <IconButton
          label="Separar um pacote para outra pessoa"
          icon={<Split />}
          onClick={onSplit}
        />
      ) : null}
      {session ? (
        <div className="grid gap-1 rounded-lg bg-brand-500/10 p-3">
          <Switch checked={session.on} onChange={session.onToggle}>
            Usar a sessão de hoje deste pacote
          </Switch>
          <p className="text-xs text-content-muted">
            {session.on
              ? `${session.serviceName} sai da venda e o atendimento de hoje passa a usar uma sessão deste pacote. A sessão só é usada se o recebido já liberar ao menos uma pela regra do pacote; se não liberar, a venda é recusada e o aviso diz quanto falta.`
              : 'O atendimento de hoje continua avulso, e o pacote começa na próxima sessão.'}
          </p>
        </div>
      ) : null}
      <div className="flex items-center justify-between gap-2">
        <Stepper
          label={line.label}
          quantity={line.quantity}
          canIncrease={canIncrease}
          onChange={onChange}
          onQuantity={onQuantity}
        />
        <strong className="tabular-nums">
          {formatCurrency(line.priceCents * line.quantity, 'BRL')}
        </strong>
      </div>
      {problem ? (
        <p role="alert" className="text-xs text-danger">
          {problem} Diminua a quantidade, troque o local ou remova o item.
        </p>
      ) : null}
      {commission}
    </li>
  );
}

function Stepper({
  label,
  quantity,
  canIncrease,
  onChange,
  onQuantity,
}: {
  label: string;
  quantity: number;
  canIncrease: boolean;
  onChange: (delta: number) => void;
  onQuantity?: ((quantity: number) => void) | undefined;
}) {
  return (
    <div className="flex items-center gap-1">
      <IconButton
        label={`Diminuir quantidade de ${label}`}
        icon={<Minus className="size-4" />}
        disabled={quantity <= 1 && Boolean(onQuantity)}
        onClick={() => onChange(-1)}
      />
      {onQuantity ? (
        <input
          aria-label={`Quantidade de ${label}`}
          inputMode="numeric"
          className="h-9 w-12 rounded-lg border border-surface-border bg-transparent text-center tabular-nums focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/50"
          value={quantity || ''}
          aria-invalid={quantity < 1 || undefined}
          onChange={(event) => {
            const text = event.target.value;
            if (/^\d*$/u.test(text)) onQuantity(Number(text));
          }}
        />
      ) : (
        <span className="w-8 text-center tabular-nums" aria-label={`${String(quantity)} na venda`}>
          {quantity}
        </span>
      )}
      <IconButton
        label={`Aumentar quantidade de ${label}`}
        icon={<Plus className="size-4" />}
        disabled={!canIncrease}
        onClick={() => onChange(1)}
      />
    </div>
  );
}

/// De onde saem os produtos: o local e, no produto com lote, a divisão do que vence primeiro ou a
/// escolhida à mão. Lote vencido aparece, mas não entra na venda (ADR 0059).
function StockBlock({
  needs,
  locations,
  locationId,
  onLocation,
  stockOf,
  problemOf,
  manual,
  onManual,
  error,
  loading,
  failure,
}: {
  needs: ReturnType<typeof stockNeeds>;
  locations: Array<{ id: string; displayName: string }>;
  locationId: string;
  onLocation: (locationId: string) => void;
  stockOf: (referenceId: string) => StockItem | undefined;
  problemOf: (need: ReturnType<typeof stockNeeds>[number]) => string | null;
  manual: Record<string, Record<string, number>>;
  onManual: (next: Record<string, Record<string, number>>) => void;
  error: string | undefined;
  loading: boolean;
  failure: string | undefined;
}) {
  const ref = useRef<HTMLElement>(null);
  useEffect(() => {
    if (error) ref.current?.scrollIntoView?.({ block: 'nearest' });
  }, [error]);
  return (
    <section
      ref={ref}
      aria-label="Saída do estoque"
      className="grid gap-2 border-t border-surface-border pt-3"
    >
      <h3 className="text-xs font-semibold uppercase tracking-wide text-content-faint">
        Saída do estoque
      </h3>
      {failure ? (
        <p role="alert" className="text-sm text-danger">
          {failure}
        </p>
      ) : !loading && locations.length === 0 ? (
        <NoStockLocationNotice />
      ) : (
        <Select
          label="Local de estoque"
          value={locationId}
          onValueChange={onLocation}
          options={locations.map((location) => ({
            value: location.id,
            label: location.displayName,
          }))}
        />
      )}
      {needs.map((need) => {
        const item = stockOf(need.referenceId);
        if (!item?.requiresLot) return null;
        const chosen = manual[need.referenceId];
        return (
          <div key={need.referenceId} className="grid gap-1 rounded-lg bg-content/5 p-2 text-xs">
            <div className="flex items-center justify-between gap-2">
              <span className="font-semibold">
                {need.label} · {chosen ? 'lotes escolhidos' : 'sai do lote que vence primeiro'}
              </span>
              <IconButton
                label={chosen ? 'Usar o que vence primeiro' : 'Escolher lotes'}
                icon={<Layers />}
                onClick={() => {
                  const next = { ...manual };
                  if (chosen) delete next[need.referenceId];
                  else
                    next[need.referenceId] = Object.fromEntries(
                      lotPlan(item.lots, need.quantity).map((part) => [part.lot.id, part.quantity]),
                    );
                  onManual(next);
                }}
              />
            </div>
            {chosen
              ? item.lots.map((lot) =>
                  lot.expired ? (
                    <p key={lot.id} className="text-content-muted">
                      <s>{lot.code}</s> · venceu{' '}
                      {lot.expiresOn ? formatCivilDate(lot.expiresOn) : ''} · fora da venda
                    </p>
                  ) : (
                    <label key={lot.id} className="flex items-center justify-between gap-2">
                      <span>
                        <strong>{lot.code}</strong>
                        {lot.expiresOn ? ` · vence ${formatCivilDate(lot.expiresOn)}` : ''} · saldo{' '}
                        {String(lot.packages ?? 0)}
                      </span>
                      <input
                        aria-label={`Quantidade do lote ${lot.code}`}
                        inputMode="numeric"
                        className="h-8 w-14 rounded-lg border border-surface-border bg-transparent text-center tabular-nums focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/50"
                        value={chosen[lot.id] ?? 0}
                        onChange={(event) => {
                          if (!/^\d*$/u.test(event.target.value)) return;
                          onManual({
                            ...manual,
                            [need.referenceId]: { ...chosen, [lot.id]: Number(event.target.value) },
                          });
                        }}
                      />
                    </label>
                  ),
                )
              : lotPlan(item.lots, need.quantity).map((part) => (
                  <p key={part.lot.id} className="flex justify-between gap-2 tabular-nums">
                    <span>
                      <strong>{part.lot.code}</strong>
                      {part.lot.expiresOn ? ` · vence ${formatCivilDate(part.lot.expiresOn)}` : ''}
                    </span>
                    <span>
                      {part.quantity} de {String(part.lot.packages ?? 0)}
                    </span>
                  </p>
                ))}
            {chosen && problemOf(need) ? (
              <p role="alert" className="text-danger">
                {problemOf(need)}
              </p>
            ) : null}
          </div>
        );
      })}
      <p className="text-xs text-content-muted">
        Os produtos saem daqui ao confirmar. Cancelar a venda devolve ao estoque.
      </p>
      {error ? (
        <p role="alert" className="text-sm text-danger">
          {error}
        </p>
      ) : null}
    </section>
  );
}

/// O catálogo da venda: tipos, busca que vale para todo o catálogo, cards ou lista e páginas.
function SaleCatalog({
  view,
  requiredServiceId,
  onView,
  locationId,
  inSale,
  onAdd,
  onChange,
}: {
  requiredServiceId?: string | undefined;
  view: SalesView;
  onView: ViewSetter<SalesView>;
  locationId: string;
  inSale: (key: string) => number;
  onAdd: (entry: Omit<CartLine, 'quantity'>, known?: number) => void;
  onChange: (key: string, delta: number) => void;
}) {
  const snackbar = useSnackbar();
  const [text, setText] = useState(view.search);
  useEffect(() => {
    const timer = setTimeout(() => {
      const search = text.trim();
      if (search !== view.search.trim())
        onView(
          { search, offset: 0, ...(search && view.kind === 'TOP' ? { kind: 'ALL' as const } : {}) },
          { replace: true },
        );
    }, SEARCH_DELAY_MS);
    return () => clearTimeout(timer);
  }, [text, view.search, view.kind, onView]);
  const sizes: readonly number[] = SALE_CATALOG_PAGE_SIZES[view.display];
  const limit = sizes.includes(view.limit) ? view.limit : sizes[0]!;
  const search = view.search.trim();
  const page = useQuery({
    queryKey: [
      'company',
      'sales',
      'catalog-page',
      view.kind,
      search,
      locationId,
      limit,
      view.offset,
    ],
    queryFn: () =>
      salesApi.catalogPage({
        kind: view.kind === 'TOP' ? 'ALL' : view.kind,
        ...(search ? { search } : {}),
        ...(locationId ? { locationId } : {}),
        limit,
        offset: view.offset,
      }),
    enabled: view.kind !== 'TOP',
    placeholderData: keepPreviousData,
    retry: false,
  });
  const featured = useQuery({
    queryKey: ['company', 'sales', 'featured'],
    queryFn: () => salesApi.featured(),
    enabled: view.kind === 'TOP',
    retry: false,
  });
  const featuredProducts = (featured.data?.items ?? []).filter((item) => item.kind === 'PRODUCT');
  const featuredStock = useQuery({
    queryKey: [
      'company',
      'sales',
      'featured-stock',
      locationId,
      featuredProducts.map((item) => item.id).join(','),
    ],
    queryFn: () =>
      salesApi.stockOptions({
        locationId: locationId || undefined,
        items: featuredProducts.map((item) => ({ referenceId: item.id, quantity: 1 })),
      }),
    enabled: view.kind === 'TOP' && featuredProducts.length > 0,
    retry: false,
  });
  const entries: SalesCatalogPageEntry[] =
    view.kind === 'TOP'
      ? (featured.data?.items ?? []).map((item) =>
          item.kind === 'PRODUCT'
            ? {
                ...item,
                stock: {
                  available:
                    featuredStock.data?.items.find((entry) => entry.referenceId === item.id)
                      ?.available ?? 0,
                  elsewhere: null,
                },
              }
            : item,
        )
      : (page.data?.items ?? []);
  const total = view.kind === 'TOP' ? entries.length : (page.data?.total ?? 0);
  const loading = view.kind === 'TOP' ? featured.isLoading : page.isLoading;
  const failure = view.kind === 'TOP' ? featured.error : page.error;

  /// O leitor de código de barras digita o código e o Enter: o item com aquele código, ou o único
  /// resultado, entra direto na venda.
  const scanSequence = useRef(0);
  const addFromReader = useRef(onAdd);
  useEffect(() => {
    addFromReader.current = onAdd;
  }, [onAdd]);
  async function submit(code: string, sequence: number) {
    function restore() {
      if (scanSequence.current === sequence) setText((current) => current || code);
    }
    const found = await salesApi
      .catalogPage({
        kind: 'ALL',
        search: code,
        ...(locationId ? { locationId } : {}),
        limit: 5,
        offset: 0,
      })
      .catch(() => undefined);
    if (!found) {
      snackbar.failure('Não foi possível buscar o item agora.');
      restore();
      return;
    }
    const hit =
      found.items.find((item) => item.kind !== 'PACKAGE' && item.barcode === code) ??
      (found.items.length === 1 ? found.items[0] : undefined);
    if (!hit) {
      if (found.items.length === 0) snackbar.failure('Nenhum item com esse nome ou EAN.');
      restore();
      return;
    }
    addFromReader.current(
      entryLine(hit),
      hit.kind === 'PRODUCT' ? hit.stock?.available : undefined,
    );
  }

  return (
    <section aria-label="Catálogo" className="grid min-w-0 gap-3">
      <div className="flex flex-wrap items-end gap-3">
        <div className="w-full min-w-0 sm:max-w-sm sm:flex-1">
          <Input
            hideLabel
            label="Buscar serviço, produto ou pacote"
            placeholder="Nome ou EAN"
            leftIcon={<Search className="size-4" />}
            value={text}
            onChange={(event) => setText(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault();
                const code = event.currentTarget.value.trim();
                if (!code) return;
                // Libera o campo antes da consulta: o próximo código não se concatena ao anterior.
                setText('');
                void submit(code, ++scanSequence.current);
              }
            }}
            hint="Com o leitor de código de barras, o item entra direto na venda."
          />
        </div>
        {!requiredServiceId ? (
          <SegmentedControl
            label="Mostrar"
            className="w-fit max-w-full [&_button]:flex-none [&_button]:basis-auto"
            value={view.kind}
            onChange={(kind: SalesCatalogKind) => {
              setText('');
              onView({ kind, search: '', offset: 0 });
            }}
            options={[
              { value: 'TOP' as const, label: 'Mais vendidos' },
              { value: 'ALL' as const, label: 'Tudo' },
              { value: 'SERVICE' as const, label: 'Serviços' },
              { value: 'PRODUCT' as const, label: 'Produtos' },
              { value: 'PACKAGE' as const, label: 'Pacotes' },
            ]}
          />
        ) : (
          <p className="text-sm text-content-muted">
            Pacotes com outros serviços aparecem indisponíveis para este agendamento.
          </p>
        )}
      </div>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p data-slot="list-count" className="text-xs text-content-faint">
          {view.kind === 'TOP'
            ? 'Mais vendidos'
            : `${String(total)} ${total === 1 ? 'item' : 'itens'}${search ? ` para “${search}”` : ''}`}
        </p>
        <div className="flex items-center gap-2">
          {view.kind !== 'TOP' ? (
            <PageSizePicker
              size={limit}
              options={sizes}
              onChange={(size) => onView({ limit: size, offset: 0 })}
            />
          ) : null}
          <Tooltip content={view.display === 'cards' ? 'Ver em lista' : 'Ver em cards'}>
            <IconButton
              label={view.display === 'cards' ? 'Ver em lista' : 'Ver em cards'}
              icon={
                view.display === 'cards' ? (
                  <List className="size-4" />
                ) : (
                  <LayoutGrid className="size-4" />
                )
              }
              onClick={() => {
                const display = view.display === 'cards' ? 'list' : 'cards';
                onView({ display, limit: SALE_CATALOG_PAGE_SIZES[display][0], offset: 0 });
              }}
            />
          </Tooltip>
        </div>
      </div>
      {loading ? (
        <LoadingCard label="Carregando o catálogo" />
      ) : failure ? (
        <ErrorCard message={getApiError(failure)} />
      ) : entries.length === 0 ? (
        <EmptyCard
          icon={PackageOpen}
          title={search ? 'Nada encontrado' : 'Nada à venda aqui'}
          description={
            search
              ? 'Confira o nome ou o EAN; a busca procura em todo o catálogo.'
              : 'Cadastre serviços, marque produtos do Estoque como vendáveis ou crie pacotes.'
          }
        />
      ) : (
        <ul
          className={
            view.display === 'cards'
              ? 'grid gap-3 sm:grid-cols-2 2xl:grid-cols-3'
              : 'grid divide-y divide-surface-border rounded-2xl border border-surface-border bg-surface-card'
          }
        >
          {entries.map((entry) => (
            <CatalogEntry
              key={`${entry.kind}:${entry.id}`}
              entry={entry}
              unavailable={
                !!requiredServiceId &&
                !entryLine(entry).balanceServiceIds.includes(requiredServiceId)
              }
              display={view.display}
              inSale={inSale(entryLine(entry).key)}
              onAdd={() =>
                onAdd(
                  entryLine(entry),
                  entry.kind === 'PRODUCT' ? entry.stock?.available : undefined,
                )
              }
              onChange={(delta) => onChange(entryLine(entry).key, delta)}
            />
          ))}
        </ul>
      )}
      {view.kind !== 'TOP' && total > 0 ? (
        <Pagination
          offset={view.offset}
          limit={limit}
          total={total}
          onOffsetChange={(offset) => onView({ offset })}
          itemLabel={(count) => (count === 1 ? 'item' : 'itens')}
        />
      ) : null}
    </section>
  );
}

function CatalogEntry({
  entry,
  unavailable = false,
  display,
  inSale,
  onAdd,
  onChange,
}: {
  unavailable?: boolean | undefined;
  entry: SalesCatalogPageEntry;
  display: 'cards' | 'list';
  inSale: number;
  onAdd: () => void;
  onChange: (delta: number) => void;
}) {
  const line = entryLine(entry);
  const priced: { price: number; base: number; promotion: string | null } =
    entry.kind === 'PACKAGE'
      ? {
          price: entry.package.promotion?.priceCents ?? entry.package.priceCents,
          base: entry.package.priceCents,
          promotion: entry.package.promotion
            ? promotionLabel(entry.package.promotion.validUntil)
            : null,
        }
      : {
          price: entry.priceCents,
          base: entry.basePriceCents,
          promotion: entry.promotion ? promotionLabel(entry.promotion.validUntil) : null,
        };
  const stock = entry.kind === 'PRODUCT' ? entry.stock : undefined;
  const soldOut = stock !== undefined && stock.available === 0 && inSale === 0;
  const canIncrease = stock === undefined || inSale < stock.available;
  const action = inSale ? (
    <Stepper
      label={entry.displayName}
      quantity={inSale}
      canIncrease={canIncrease}
      onChange={onChange}
    />
  ) : (
    <IconButton
      label={`Adicionar ${entry.displayName}`}
      icon={<Plus />}
      disabled={soldOut || unavailable}
      onClick={onAdd}
    />
  );
  const tags = (
    <div className="flex flex-wrap items-center gap-1.5">
      {stock ? (
        <StatusPill tone={stock.available > 0 ? 'positive' : 'danger'}>
          {stock.available > 0 ? `${String(stock.available)} em estoque` : 'Sem saldo aqui'}
        </StatusPill>
      ) : null}
      {inSale ? <StatusPill tone="brand">{inSale} na venda</StatusPill> : null}
    </div>
  );
  const price = (
    <span className="grid justify-items-end">
      {priced.promotion ? (
        <s className="text-xs text-content-muted">{formatCurrency(priced.base, 'BRL')}</s>
      ) : null}
      <strong className="tabular-nums">{formatCurrency(priced.price, 'BRL')}</strong>
    </span>
  );
  const details = (
    <>
      {unavailable ? (
        <p className="text-xs text-content-muted">
          Não inclui sessões por saldo do serviço selecionado.
        </p>
      ) : null}
      {line.summary ? <p className="text-xs text-content-muted">{line.summary}</p> : null}
      {priced.promotion ? <p className="text-xs text-content-muted">{priced.promotion}</p> : null}
      {soldOut && stock?.elsewhere ? (
        <p className="text-xs text-content-muted">
          Há {stock.elsewhere.available} em {stock.elsewhere.displayName}
        </p>
      ) : null}
    </>
  );
  if (display === 'list')
    return (
      <li
        className={`flex flex-wrap items-center justify-between gap-3 p-3 ${inSale ? 'bg-brand-500/5' : ''}`}
      >
        <div className="min-w-0 flex-1">
          <span className="text-xs text-content-muted">{KIND_LABEL[entry.kind]}</span>
          {tags}
          <p className="font-semibold">{entry.displayName}</p>
          {details}
        </div>
        <div className="flex items-center gap-3">
          {price}
          {action}
        </div>
      </li>
    );
  return (
    <li
      className={`flex flex-col justify-between gap-3 rounded-2xl border bg-surface-card p-4 ${inSale ? 'border-brand-500/60' : 'border-surface-border'}`}
    >
      <div className="grid min-w-0 gap-1">
        <span className="text-xs text-content-muted">{KIND_LABEL[entry.kind]}</span>
        {tags}
        <h3 className="font-semibold">{entry.displayName}</h3>
        {details}
      </div>
      <div className="flex items-center justify-between gap-2">
        {price}
        {action}
      </div>
    </li>
  );
}

function promotionLabel(validUntil: string | null): string {
  return validUntil ? `Promoção até ${formatCivilDate(validUntil)}` : 'Promoção sem data de fim';
}

/// Um campo só para o cliente: com CPF completo e válido, a consulta é exata; com letras, busca
/// pelo nome. Poucos resultados por vez, nunca a lista inteira.
function ClientPicker({
  label,
  selected,
  onSelect,
  hint,
  hideLabel = false,
}: {
  label: string;
  selected: Person | null;
  onSelect: (person: Person | null) => void;
  hint: string;
  /// O bloco já diz que é o cliente; o rótulo fica para o leitor de tela.
  hideLabel?: boolean;
}) {
  const [text, setText] = useState('');
  const [query, setQuery] = useState('');
  useEffect(() => {
    const timer = setTimeout(() => setQuery(text.trim()), SEARCH_DELAY_MS);
    return () => clearTimeout(timer);
  }, [text]);
  const cpf = digitsOf(query) ?? '';
  const numeric = /^[\d.\-\s]+$/u.test(query);
  const byCpf = numeric && isValidCpf(cpf);
  const byName = !numeric && (query.match(/\p{L}/gu)?.length ?? 0) >= 2;
  const result = useQuery({
    queryKey: ['company', 'sales', 'client-search', byCpf ? `cpf:${cpf}` : query],
    queryFn: () =>
      clientsApi.list({
        ...(byCpf ? { cpf, kind: 'PERSON' as const } : { search: query, kind: 'ALL' as const }),
        activity: 'ACTIVE',
        access: 'ALL',
        sort: 'displayName',
        direction: 'asc',
        limit: 20,
        offset: 0,
      }),
    enabled: byCpf || byName,
    retry: false,
  });
  const clients = byCpf || byName ? (result.data?.clients ?? []) : [];
  const status = !query
    ? undefined
    : numeric && !byCpf
      ? 'O CPF busca depois de completo e válido.'
      : !numeric && !byName
        ? 'Digite ao menos 2 letras do nome.'
        : result.data
          ? `${String(result.data.total)} ${result.data.total === 1 ? 'cliente' : 'clientes'}`
          : undefined;
  return (
    <Combobox
      label={label}
      placeholder="Nome ou CPF"
      selectedLabel={selected?.name ?? ''}
      options={clients.map((found) => ({
        value: found.id,
        label: found.displayName,
        description: [found.phone, found.document].filter(Boolean).join(' · ') || undefined,
      }))}
      onSelect={(choice) => {
        if (choice.kind !== 'option') return;
        const found = clients.find((entry) => entry.id === choice.value);
        if (found) onSelect({ id: found.id, name: found.displayName });
      }}
      onQueryChange={setText}
      loading={result.isFetching}
      status={byCpf || byName ? status : undefined}
      onClear={() => onSelect(null)}
      emptyLabel={
        !text.trim()
          ? 'Digite o nome ou o CPF do cliente'
          : !(byCpf || byName)
            ? (status ?? 'Digite o nome ou o CPF do cliente')
            : 'Nenhum cliente encontrado'
      }
      hint={hint}
      hideLabel={hideLabel}
    />
  );
}

function CardPaymentFields({
  installmentCount,
  onCount,
  onValid,
}: {
  installmentCount: number;
  onCount: (value: number) => void;
  onValid: (value: boolean) => void;
}) {
  const conditions = useQuery({
    queryKey: ['company', 'sale-card-policy'],
    queryFn: () => cardsApi.salePolicy(),
    retry: false,
  });
  const available = conditions.data?.installmentCounts;
  useEffect(() => {
    onValid(
      Boolean(available?.includes(installmentCount)) && !conditions.isFetching && !conditions.error,
    );
    if (available?.length && !available.includes(installmentCount)) onCount(available[0]!);
  }, [available, installmentCount, conditions.isFetching, conditions.error, onCount, onValid]);
  if (conditions.isLoading) return <LoadingCard label="Carregando parcelas disponíveis" />;
  if (conditions.error) return <RequestAlert message={getApiError(conditions.error)} />;
  return (
    <div className="grid gap-2">
      <Select
        label="Parcelas no cartão"
        value={String(installmentCount)}
        onValueChange={(value) => onCount(Number(value))}
        options={(available ?? []).map((count) => ({
          value: String(count),
          label: `${String(count)}×`,
        }))}
      />
      {!available?.length ? (
        <RequestAlert message="O cartão ainda não possui parcelas disponíveis. Solicite a configuração ao responsável financeiro." />
      ) : null}
    </div>
  );
}
